import { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import LogViewer from '../components/LogViewer';
import AgentLogViewer from '../components/AgentLogViewer';
import { API_URL } from '../services/api';
import { useApiHeaders } from '../hooks/useApiHeaders';
import { computeSecurityRisk, type SecurityRiskResult, type RiskLevel } from '../utils/securityRisk';
import { BOT_TYPES, botTypeRequiresWorkspace } from '../lib/botTypes';
import WorkspacePicker from '../components/WorkspacePicker';
import ShareUrlInput from '../components/ShareUrlInput';
import RootMigrationBanner from '../components/RootMigrationBanner';

interface Agent {
	agent_id: string;
	hostname: string;
	platform: string;
	version: string;
	status: 'online' | 'offline';
	last_heartbeat: number;
	created_at: number;
	latest_version?: string;
	update_available?: boolean;
	openclaw_installed?: number | null;
	// 1 = legacy root LaunchDaemon / system-level systemd.
	// 0 = user-level install.
	// null = unknown (older connector that doesn't report).
	running_as_root?: number | null;
	local_ips?: string | null;
	last_ip?: string | null;
}

interface BotInstance {
	instance_id: string;
	agent_id: string;
	instance_name?: string;
	gateway_port?: number;
	bot_type?: string;
	clawdbot_version: string;
	install_path: string;
	running_status: 'running' | 'stopped' | 'error';
	agent_status?: 'online' | 'offline';
	discovered_at?: number;
	created_at?: number;
}

const BOT_TYPE_LABELS: Record<string, { label: string; icon: string }> = {
	openclaw: { label: 'OpenClaw', icon: 'claw' },
	'claude-code': { label: 'Claude Code', icon: 'terminal' },
	claude_code: { label: 'Claude Code', icon: 'terminal' },
	codex: { label: 'Codex', icon: 'code' },
	cursor: { label: 'Cursor', icon: 'cursor' },
	custom: { label: 'Custom', icon: 'puzzle' },
};

function getBotTypeLabel(type: string): string {
	return BOT_TYPE_LABELS[type]?.label || type.charAt(0).toUpperCase() + type.slice(1);
}

function getPlatformIcon(platform: string, className: string) {
	const p = platform.toLowerCase();
	if (p.includes('darwin') || p.includes('macos')) {
		return (
			<svg className={className} viewBox="0 0 24 24" fill="currentColor">
				<path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
			</svg>
		);
	}
	if (p.includes('windows')) {
		return (
			<svg className={className} viewBox="0 0 24 24" fill="currentColor">
				<path d="M3 12V6.75l8-1.25V12H3zm0 .5h8v6.5l-8-1.25V12.5zM11.5 5.34l9.5-1.34v8h-9.5V5.34zM11.5 12.5H21v7.84l-9.5-1.34V12.5z"/>
			</svg>
		);
	}
	// Linux
	return (
		<svg className={className} viewBox="0 0 24 24" fill="currentColor">
			<path d="M12.504 0c-.155 0-.315.008-.48.021-4.226.333-3.105 4.807-3.17 6.298-.076 1.092-.3 1.953-1.05 3.02-.885 1.051-2.127 2.75-2.716 4.521-.278.832-.41 1.684-.287 2.489a.424.424 0 00-.11.135c-.26.268-.45.6-.663.839-.199.199-.485.267-.797.4-.313.136-.658.269-.864.68-.09.189-.136.394-.132.602 0 .199.027.4.055.536.058.399.116.728.04.97-.249.68-.28 1.145-.106 1.484.174.334.535.47.94.601.81.2 1.91.135 2.774.6.926.466 1.866.67 2.616.47.526-.116.97-.464 1.208-.946.587-.003 1.23-.269 2.26-.334.699-.058 1.574.267 2.577.2.025.134.063.198.114.333l.003.003c.391.778 1.113 1.368 1.884 1.43.868.065 1.326-.53 1.441-.813.188-.39.025-.975-.254-1.603-.267-.585-.61-1.135-.529-1.404l.003-.003c.458-.396.91-1.069 1.148-1.6.009-.065.014-.129.014-.193 0-.275-.114-.557-.378-.756-.128-.097-.276-.159-.418-.202.108-.39.105-.765.012-1.12-.108-.41-.296-.774-.496-1.095-.401-.599-.936-1.254-.96-2.284-.026-.665.06-1.297.236-1.867a5.79 5.79 0 00-.14-.849c-.39-1.899-1.39-3.064-2.333-4.167-.945-1.103-1.98-2.163-2.15-3.835-.084-.843-.009-1.715.174-2.475C14.324.727 13.663.003 12.504 0z"/>
		</svg>
	);
}

function getBotTypeIcon(botType: string | undefined, className: string) {
	const type = botType?.toLowerCase() || '';
	if (type === 'openclaw') {
		return (
			<img src="/img/openclaw-logo.png" alt="OpenClaw" className={className + ' rounded'} />
		);
	}
	if (type === 'claude-code' || type === 'claude_code') {
		// Terminal icon for Claude Code
		return (
			<svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
				<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
			</svg>
		);
	}
	if (type === 'codex') {
		// Code brackets icon for Codex
		return (
			<svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
				<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
			</svg>
		);
	}
	if (type === 'cursor') {
		// Cursor/pointer icon
		return (
			<svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
				<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
			</svg>
		);
	}
	// Generic/custom — puzzle piece
	return (
		<svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
		</svg>
	);
}

function SecurityBadge({ risk, label, icon }: { risk: RiskLevel; label: string; icon: 'shield' | 'lock' | 'warning' }) {
	const colorClass = risk === 'good'
		? 'text-green-600 dark:text-green-400'
		: risk === 'warning'
		? 'text-yellow-600 dark:text-yellow-400'
		: 'text-red-600 dark:text-red-400';

	const bgClass = risk === 'good'
		? 'bg-green-50 dark:bg-green-900/20'
		: risk === 'warning'
		? 'bg-yellow-50 dark:bg-yellow-900/20'
		: 'bg-red-50 dark:bg-red-900/20';

	return (
		<span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-md text-xs font-medium ${colorClass} ${bgClass}`}>
			{icon === 'shield' && (
				<svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
				</svg>
			)}
			{icon === 'lock' && (
				<svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
				</svg>
			)}
			{icon === 'warning' && (
				<svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
				</svg>
			)}
			<span>{label}</span>
		</span>
	);
}

export default function MachineDetailPage() {
	const { agentId } = useParams<{ agentId: string }>();
	const navigate = useNavigate();
	const { firebaseUser } = useAuth();
	const { getHeaders } = useApiHeaders();
	const [agent, setAgent] = useState<Agent | null>(null);
	const [instances, setInstances] = useState<BotInstance[]>([]);
	const [loading, setLoading] = useState(true);
	const [installing, setInstalling] = useState(false);
	const [showAddInstanceModal, setShowAddInstanceModal] = useState(false);
	const [newInstanceName, setNewInstanceName] = useState('');
	const [newInstanceType, setNewInstanceType] = useState('openclaw');
	// Workspace step state — only used for claude_code / codex.
	const [newWorkspaceMode, setNewWorkspaceMode] = useState<'local' | 'remote'>('local');
	const [newWorkspaceDir, setNewWorkspaceDir] = useState('');
	const [newShareUrl, setNewShareUrl] = useState('');
	const [addingInstance, setAddingInstance] = useState(false);
	const [actionLoading, setActionLoading] = useState<string | null>(null);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [deleting, setDeleting] = useState(false);
	const [deleteConfirmText, setDeleteConfirmText] = useState('');
	const [activeCommandId, setActiveCommandId] = useState<string | null>(null);
	const [showLogs, setShowLogs] = useState(false);
	const [logMode, setLogMode] = useState<'log' | 'terminal'>('log');
	// 'machine' = show under machine card, instanceId = show under that instance card
	const [terminalContext, setTerminalContext] = useState<'machine' | string | null>(null);
	const [showCleanConfirm, setShowCleanConfirm] = useState(false);
	const [showUninstallConfirm, setShowUninstallConfirm] = useState<string | null>(null); // instance_id
	const [renamingInstanceId, setRenamingInstanceId] = useState<string | null>(null);
	const [renameValue, setRenameValue] = useState('');
	const [showAgentLogs, setShowAgentLogs] = useState(false);
	const [instanceLogsId, setInstanceLogsId] = useState<string | null>(null);

	// Per-instance security risk data
	const [instanceRisks, setInstanceRisks] = useState<Record<string, SecurityRiskResult>>({});

	// Channel key assignments for this agent
	type ChannelKeyAssignment = { instance_id?: string; instance_name?: string; agent_id: string; channel_type: string; key_id: string; label: string; assigned_at: number };
	const [channelKeyAssignments, setChannelKeyAssignments] = useState<ChannelKeyAssignment[]>([]);

	// Bot type tab state
	const [activeBotType, setActiveBotType] = useState<string>('all');

	// Inline config state

	// Group instances by bot_type
	const instancesByType = useMemo(() => {
		const grouped: Record<string, BotInstance[]> = {};
		for (const inst of instances) {
			const type = inst.bot_type || 'openclaw';
			(grouped[type] ??= []).push(inst);
		}
		return grouped;
	}, [instances]);

	const botTypes = useMemo(() => Object.keys(instancesByType).sort(), [instancesByType]);

	const displayedInstances = useMemo(() => {
		if (activeBotType === 'all') return instances;
		return instancesByType[activeBotType] || [];
	}, [activeBotType, instances, instancesByType]);

	// Fetch agent details
	useEffect(() => {
		let cancelled = false;

		const fetchData = async () => {
			if (!firebaseUser || !agentId) return;

			try {
				const headers = await getHeaders();

				const agentsResponse = await fetch(`${API_URL}/api/v1/agents`, {
					headers: headers,
				});

				if (cancelled) return;

				if (agentsResponse.ok) {
					const agentsData = await agentsResponse.json();
					const foundAgent = agentsData.agents?.find((a: Agent) => a.agent_id === agentId);
					setAgent(foundAgent || null);
				}

				const instancesResponse = await fetch(`${API_URL}/api/v1/instances`, {
					headers: headers,
				});

				if (cancelled) return;

				if (instancesResponse.ok) {
					const instancesData = await instancesResponse.json();
					const agentInstances = instancesData.instances?.filter((i: BotInstance) => i.agent_id === agentId) || [];
					setInstances(agentInstances);
				}
			} catch (error) {
				if (!cancelled) console.error('Error fetching data:', error);
			} finally {
				if (!cancelled) setLoading(false);
			}
		};

		fetchData();
		const interval = setInterval(fetchData, 15000);

		return () => {
			cancelled = true;
			clearInterval(interval);
		};
	}, [firebaseUser, agentId]);

	// Fetch per-agent security summary
	useEffect(() => {
		let cancelled = false;

		const fetchSecurityRisks = async () => {
			if (!firebaseUser || !agentId) return;

			try {
				const headers = await getHeaders();
				const response = await fetch(`${API_URL}/api/v1/policies/security-summary?agent_id=${agentId}`, {
					headers: headers,
				});

				if (cancelled) return;

				if (response.ok) {
					const data = await response.json();
					const risks: Record<string, SecurityRiskResult> = {};
					for (const inst of data.instances || []) {
						risks[inst.instance_id] = computeSecurityRisk(inst.config, inst.gateway_assessment, inst.gateway_assessed_at);
					}
					setInstanceRisks(risks);
				}
			} catch (error) {
				if (!cancelled) console.error('Error fetching security risks:', error);
			}
		};

		fetchSecurityRisks();
		const interval = setInterval(fetchSecurityRisks, 30000);

		return () => {
			cancelled = true;
			clearInterval(interval);
		};
	}, [firebaseUser, agentId]);

	// Fetch channel key assignments for this agent
	useEffect(() => {
		let cancelled = false;

		const fetchAssignments = async () => {
			if (!firebaseUser || !agentId) return;
			try {
				const headers = await getHeaders();
				const response = await fetch(`${API_URL}/api/v1/channel-keys/assignments`, { headers });
				if (cancelled) return;
				if (response.ok) {
					const data = await response.json();
					// Filter to only this agent's assignments
					const agentAssignments = (data.assignments || []).filter(
						(a: ChannelKeyAssignment) => a.agent_id === agentId
					);
					setChannelKeyAssignments(agentAssignments);
				}
			} catch (error) {
				if (!cancelled) console.error('Error fetching channel key assignments:', error);
			}
		};

		fetchAssignments();
		return () => { cancelled = true; };
	}, [firebaseUser, agentId]);

	const handleInstall = async (installMode: 'clean' | 'upgrade' = 'upgrade') => {
		if (!firebaseUser || !agentId) return;

		setInstalling(true);
		setShowLogs(true);
		setLogMode('terminal');
		setTerminalContext('machine');
		setShowCleanConfirm(false);

		try {
			const headers = await getHeaders();
			const response = await fetch(`${API_URL}/api/v1/instances/install`, {
				method: 'POST',
				headers: headers,
				body: JSON.stringify({ agent_id: agentId, install_mode: installMode }),
			});

			if (response.ok) {
				const data = await response.json();
				if (data.command_id) {
					setActiveCommandId(data.command_id);
				}
				setTimeout(async () => {
					const instancesResponse = await fetch(`${API_URL}/api/v1/instances`, {
						headers: headers,
					});
					if (instancesResponse.ok) {
						const data = await instancesResponse.json();
						const agentInstances = data.instances?.filter((i: BotInstance) => i.agent_id === agentId) || [];
						setInstances(agentInstances);
					}
					setInstalling(false);
				}, 15000);
			} else {
				const error = await response.json();
				console.error('Install failed:', error);
				setInstalling(false);
			}
		} catch (error) {
			console.error('Install error:', error);
			setInstalling(false);
			setShowLogs(false);
			setTerminalContext(null);
		}
	};

	const handleLifecycleAction = async (instanceId: string, action: 'start' | 'stop' | 'restart') => {
		if (!firebaseUser) return;

		setActionLoading(instanceId);
		setShowLogs(true);
		setLogMode('log');
		setTerminalContext(instanceId);

		try {
			const headers = await getHeaders();
			const response = await fetch(`${API_URL}/api/v1/instances/${instanceId}/${action}`, {
				method: 'POST',
				headers: headers,
			});

			if (response.ok) {
				const data = await response.json();
				if (data.command_id) {
					setActiveCommandId(data.command_id);
				}
			}

			setTimeout(async () => {
				const response = await fetch(`${API_URL}/api/v1/instances`, {
					headers: headers,
				});
				if (response.ok) {
					const data = await response.json();
					const agentInstances = data.instances?.filter((i: BotInstance) => i.agent_id === agentId) || [];
					setInstances(agentInstances);
				}
				setActionLoading(null);
			}, 2000);
		} catch (error) {
			console.error('Action error:', error);
			setActionLoading(null);
			setShowLogs(false);
			setTerminalContext(null);
		}
	};

	const handleUninstallInstance = async (instanceId: string) => {
		if (!firebaseUser) return;

		setActionLoading(instanceId);
		setShowUninstallConfirm(null);

		try {
			const headers = await getHeaders();
			const response = await fetch(`${API_URL}/api/v1/instances/${instanceId}`, {
				method: 'DELETE',
				headers: headers,
			});

			if (response.ok) {
				// Remove from local state immediately
				setInstances(prev => prev.filter(i => i.instance_id !== instanceId));
			} else {
				const error = await response.json();
				console.error('Uninstall failed:', error);
			}
		} catch (error) {
			console.error('Uninstall error:', error);
		} finally {
			setActionLoading(null);
		}
	};

	const formatDate = (timestamp?: number) => {
		if (!timestamp) return 'Unknown';
		return new Date(timestamp).toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		});
	};

	const handleAgentUpdate = async () => {
		if (!firebaseUser || !agent) return;
		setActionLoading('agent-update');
		setShowLogs(true);
		setTerminalContext('machine');

		try {
			const headers = await getHeaders();
			const response = await fetch(`${API_URL}/api/v1/agents/${agent.agent_id}/update`, {
				method: 'POST',
				headers: headers,
				body: JSON.stringify({ version: agent.latest_version || 'latest' }),
			});

			if (response.ok) {
				const data = await response.json();
				if (data.command_id) {
					setActiveCommandId(data.command_id);
				}
			} else {
				console.error('Failed to queue agent update');
				setShowLogs(false);
				setTerminalContext(null);
			}
		} catch (error) {
			console.error('Error updating agent:', error);
			setShowLogs(false);
			setTerminalContext(null);
		} finally {
			setActionLoading(null);
		}
	};

	const handleAgentRestart = async () => {
		if (!firebaseUser || !agent) return;
		setActionLoading('agent-restart');
		setShowLogs(true);
		setTerminalContext('machine');

		try {
			const headers = await getHeaders();
			const response = await fetch(`${API_URL}/api/v1/agents/${agent.agent_id}/restart`, {
				method: 'POST',
				headers: headers,
			});

			if (response.ok) {
				const data = await response.json();
				if (data.command_id) {
					setActiveCommandId(data.command_id);
				}
			} else {
				console.error('Failed to queue agent restart');
				setShowLogs(false);
				setTerminalContext(null);
			}
		} catch (error) {
			console.error('Error restarting agent:', error);
			setShowLogs(false);
			setTerminalContext(null);
		} finally {
			setActionLoading(null);
		}
	};

	const handleDeleteMachine = async () => {
		if (!firebaseUser || !agentId || deleteConfirmText !== agent?.hostname) return;

		setDeleting(true);
		try {
			const headers = await getHeaders();
			const response = await fetch(`${API_URL}/api/v1/agents/${agentId}`, {
				method: 'DELETE',
				headers: headers,
			});

			if (response.ok) {
				navigate('/machines');
			} else {
				const error = await response.json();
				console.error('Delete failed:', error);
				setDeleting(false);
			}
		} catch (error) {
			console.error('Delete error:', error);
			setDeleting(false);
		}
	};

	const handleAddInstance = async () => {
		if (!firebaseUser || !agentId || !newInstanceName.trim()) return;
		if (!/^[a-zA-Z0-9_-]{1,64}$/.test(newInstanceName)) return;

		// Workspace validation for claude_code / codex.
		const needsWorkspace = botTypeRequiresWorkspace(newInstanceType);
		if (needsWorkspace) {
			if (newWorkspaceMode === 'local' && !newWorkspaceDir.trim()) return;
			if (newWorkspaceMode === 'remote' && !newShareUrl.trim()) return;
		}

		setAddingInstance(true);

		try {
			const headers = await getHeaders();
			const body: Record<string, unknown> = {
				agent_id: agentId,
				instance_name: newInstanceName.trim(),
				bot_type: newInstanceType,
			};
			if (needsWorkspace) {
				if (newWorkspaceMode === 'local') {
					body.workspace_dir = newWorkspaceDir.trim();
				} else {
					body.workspace_share_url = newShareUrl.trim();
				}
			}
			const response = await fetch(`${API_URL}/api/v1/instances/create`, {
				method: 'POST',
				headers: { ...headers, 'Content-Type': 'application/json' },
				body: JSON.stringify(body),
			});

			if (response.ok) {
				setShowAddInstanceModal(false);
				setNewInstanceName('');
				setNewInstanceType('openclaw');
				setNewWorkspaceMode('local');
				setNewWorkspaceDir('');
				setNewShareUrl('');
				setTimeout(async () => {
					const instancesResponse = await fetch(`${API_URL}/api/v1/instances`, {
						headers: await getHeaders(),
					});
					if (instancesResponse.ok) {
						const data = await instancesResponse.json();
						const agentInstances = data.instances?.filter((i: BotInstance) => i.agent_id === agentId) || [];
						setInstances(agentInstances);
					}
				}, 3000);
			} else {
				const error = await response.json();
				console.error('Create instance failed:', error);
			}
		} catch (error) {
			console.error('Create instance error:', error);
		} finally {
			setAddingInstance(false);
		}
	};

	const handleRenameInstance = async (instanceId: string, newName: string) => {
		if (!firebaseUser || !newName.trim() || !/^[a-zA-Z0-9_-]{1,64}$/.test(newName)) return;
		try {
			const headers = await getHeaders();
			const response = await fetch(`${API_URL}/api/v1/instances/${instanceId}/rename`, {
				method: 'PUT',
				headers: { ...headers, 'Content-Type': 'application/json' },
				body: JSON.stringify({ instance_name: newName.trim() }),
			});
			if (response.ok) {
				setInstances(prev => prev.map(inst =>
					inst.instance_id === instanceId ? { ...inst, instance_name: newName.trim() } : inst
				));
			} else {
				const error = await response.json();
				console.error('Rename failed:', error);
			}
		} catch (error) {
			console.error('Rename error:', error);
		}
		setRenamingInstanceId(null);
	};

	const getStatusBadge = (status: string, agentStatus?: string) => {
		if (agentStatus === 'offline') {
			return (
				<span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
					<span className="w-2 h-2 bg-slate-400 rounded-full mr-2"></span>
					Unavailable (Connector Offline)
				</span>
			);
		}

		switch (status) {
			case 'running':
				return (
					<span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
						<span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
						Running
					</span>
				);
			case 'stopped':
				return (
					<span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
						<span className="w-2 h-2 bg-slate-400 rounded-full mr-2"></span>
						Stopped
					</span>
				);
			case 'error':
				return (
					<span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
						<span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
						Error
					</span>
				);
			default:
				return null;
		}
	};

	if (loading) {
		return (
			<Layout title="Device Details" subtitle="Loading...">
				<div className="flex items-center justify-center h-64">
					<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
				</div>
			</Layout>
		);
	}

	if (!agent) {
		return (
			<Layout title="Device Not Found" subtitle="The requested device could not be found">
				<div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-12 text-center">
					<p className="text-slate-600 dark:text-slate-400 mb-4">This device doesn't exist or you don't have access to it.</p>
					<Link to="/machines" className="text-primary-600 hover:text-primary-700 font-medium">
						Back to Devices
					</Link>
				</div>
			</Layout>
		);
	}

	return (
		<Layout
			title={agent.hostname}
			subtitle="Manage bots and workspaces on this device"
		>
			{/* Breadcrumb */}
			<div className="mb-6">
				<Link to="/machines" className="text-sm text-slate-500 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 flex items-center">
					<svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
					</svg>
					Back to Devices
				</Link>
			</div>

			{/* Running-as-root Banner */}
			{agent.running_as_root === 1 && agent.status === 'online' && (
				<RootMigrationBanner />
			)}

			{/* Connector Upgrade Banner */}
			{agent.update_available && agent.status === 'online' && (
				<div className="mb-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-xl p-4">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-3">
							<div className="p-2 bg-amber-100 dark:bg-amber-800/40 rounded-lg">
								<svg className="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
								</svg>
							</div>
							<div>
								<p className="font-semibold text-amber-800 dark:text-amber-300">
									New Connector Version Available
								</p>
								<p className="text-sm text-amber-700 dark:text-amber-400">
									Current: v{agent.version} — Latest: v{agent.latest_version}
								</p>
							</div>
						</div>
						<button
							onClick={handleAgentUpdate}
							disabled={actionLoading === 'agent-update'}
							className="px-5 py-2.5 bg-amber-600 hover:bg-amber-700 disabled:bg-amber-400 text-white font-semibold rounded-lg transition-colors whitespace-nowrap"
						>
							{actionLoading === 'agent-update' ? 'Updating...' : `Update to v${agent.latest_version}`}
						</button>
					</div>
				</div>
			)}

			{/* Device Status Card */}
			<div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 mb-6 overflow-x-auto">
				<div className="flex items-center justify-between gap-6 min-w-max">
					<div className="flex items-center space-x-4 shrink-0">
						<div className={`p-3 rounded-xl ${agent.status === 'online' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-slate-100 dark:bg-slate-800'}`}>
							{getPlatformIcon(agent.platform, `w-8 h-8 ${agent.status === 'online' ? 'text-green-600 dark:text-green-400' : 'text-slate-400'}`)}
						</div>
						<div>
							<div className="flex items-center space-x-3">
								<h2 className="text-xl font-bold text-slate-900 dark:text-white">{agent.hostname}</h2>
								<span className={`px-3 py-1 rounded-full text-xs font-medium ${
									agent.status === 'online'
										? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
										: 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
								}`}>
									{agent.status === 'online' ? 'Online' : 'Offline'}
								</span>
							</div>
							<p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
								Connector ID: <code className="bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded text-xs">{agent.agent_id}</code>
							</p>
						</div>
					</div>

					<div className="flex flex-col gap-2 shrink-0 overflow-x-auto">
						{/* Clawdgate Agent Actions */}
						<div className="flex items-center gap-2 flex-nowrap">
							<span className="w-20 text-left text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider whitespace-nowrap shrink-0">Connector</span>
							<div className="h-5 w-px bg-slate-200 dark:bg-slate-700 shrink-0"></div>
							<div className="flex items-center gap-2 flex-nowrap">
								<button
									onClick={handleAgentUpdate}
									disabled={actionLoading === 'agent-update' || agent.status !== 'online'}
									className={`min-w-[8rem] px-4 py-2 text-sm text-white rounded-xl font-medium transition-colors cursor-pointer disabled:cursor-not-allowed flex items-center justify-center space-x-2 whitespace-nowrap ${
										agent.update_available
											? 'bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300'
											: 'bg-slate-600 hover:bg-slate-700 disabled:bg-slate-400'
									}`}
									title={agent.update_available ? `Upgrade connector to v${agent.latest_version}` : 'Re-install the latest connector version'}
								>
									{actionLoading === 'agent-update' ? (
										<>
											<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
											<span>Updating...</span>
										</>
									) : (
										<>
											<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
											</svg>
											<span>{agent.update_available ? `Update to v${agent.latest_version}` : 'Update'}</span>
										</>
									)}
								</button>
								<button
									onClick={handleAgentRestart}
									disabled={actionLoading === 'agent-restart' || agent.status !== 'online'}
									className="min-w-[8rem] px-4 py-2 text-sm bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-50 text-slate-700 dark:text-slate-300 rounded-xl font-medium transition-colors cursor-pointer disabled:cursor-not-allowed flex items-center justify-center space-x-2 whitespace-nowrap"
									title="Restart the Clawdgate connector service"
								>
									{actionLoading === 'agent-restart' ? (
										<>
											<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-600"></div>
											<span>Restarting...</span>
										</>
									) : (
										<>
											<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
											</svg>
											<span>Restart</span>
										</>
									)}
								</button>
								<button
									onClick={() => setShowDeleteModal(true)}
									className="min-w-[8rem] px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl font-medium transition-colors cursor-pointer flex items-center justify-center space-x-2 whitespace-nowrap"
								>
									<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
									</svg>
									<span>Remove</span>
								</button>
								<button
									onClick={() => setShowAgentLogs(prev => !prev)}
									disabled={agent.status !== 'online'}
									className="min-w-[8rem] px-4 py-2 text-sm bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-50 text-slate-700 dark:text-slate-300 rounded-xl font-medium transition-colors cursor-pointer disabled:cursor-not-allowed flex items-center justify-center space-x-2 whitespace-nowrap"
									title="View the connector's disk log"
								>
									<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
									</svg>
									<span>Connector Logs</span>
								</button>
							</div>
						</div>

						{/* OpenClaw Actions */}
						<div className="flex items-center gap-2 flex-nowrap">
							<span className="w-20 text-left text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider whitespace-nowrap shrink-0">OpenClaw</span>
							<div className="h-5 w-px bg-slate-200 dark:bg-slate-700 shrink-0"></div>
							<div className="flex items-center gap-2 flex-nowrap">
								{instances.length > 0 ? (
									<button
										onClick={() => handleInstall('upgrade')}
										disabled={installing || agent.status !== 'online'}
										className="min-w-[8rem] px-4 py-2 text-sm bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 disabled:from-slate-400 disabled:to-slate-500 text-white rounded-xl font-medium shadow-lg hover:shadow-xl disabled:shadow-none transition-all duration-200 cursor-pointer disabled:cursor-not-allowed flex items-center justify-center space-x-2 whitespace-nowrap"
										title="Upgrade OpenClaw: preserves configuration and tokens"
									>
										{installing ? (
											<>
												<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
												<span>Upgrading...</span>
											</>
										) : (
											<>
												<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
												</svg>
												<span>Upgrade</span>
											</>
										)}
									</button>
								) : (
									<button
										onClick={() => handleInstall('upgrade')}
										disabled={installing || agent.status !== 'online'}
										className="min-w-[8rem] px-4 py-2 text-sm bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 disabled:from-slate-400 disabled:to-slate-500 text-white rounded-xl font-medium shadow-lg hover:shadow-xl disabled:shadow-none transition-all duration-200 cursor-pointer disabled:cursor-not-allowed flex items-center justify-center space-x-2 whitespace-nowrap"
										title="Install or upgrade OpenClaw: preserves existing configuration if present"
									>
										{installing ? (
											<>
												<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
												<span>Installing...</span>
											</>
										) : (
											<>
												<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
												</svg>
												<span>Install</span>
											</>
										)}
									</button>
								)}
								<button
									onClick={() => setShowCleanConfirm(true)}
									disabled={installing || agent.status !== 'online'}
									className="min-w-[8rem] px-4 py-2 text-sm bg-red-600 hover:bg-red-700 disabled:bg-slate-400 text-white rounded-xl font-medium shadow-lg hover:shadow-xl disabled:shadow-none transition-all duration-200 cursor-pointer disabled:cursor-not-allowed flex items-center justify-center space-x-2 whitespace-nowrap"
									title="Clean Install OpenClaw: removes all config and data"
								>
									<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
									</svg>
									<span>Clean Install</span>
								</button>
							</div>
						</div>
					</div>
				</div>

				{/* Machine Info Grid */}
				<div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6 pt-6 border-t border-slate-100 dark:border-slate-800">
					<div>
						<p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">Platform</p>
						<p className="text-sm font-medium text-slate-900 dark:text-white mt-1">{agent.platform}</p>
					</div>
					<div>
						<p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">Connector Version</p>
						<div className="flex items-center gap-2 mt-1">
							<p className="text-sm font-medium text-slate-900 dark:text-white">v{agent.version}</p>
							{agent.update_available && (
								<span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
									v{agent.latest_version} available
								</span>
							)}
						</div>
					</div>
					<div>
						<p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">IP Address</p>
						<div className="mt-1">
							{agent.last_ip && (
								<p className="text-sm font-medium text-slate-900 dark:text-white">{agent.last_ip}</p>
							)}
							{(() => {
								try {
									const ips: string[] = agent.local_ips ? JSON.parse(agent.local_ips) : [];
									return ips.length > 0 ? (
										<p className="text-xs text-slate-500 dark:text-slate-400">LAN: {ips.join(', ')}</p>
									) : null;
								} catch {
									return null;
								}
							})()}
							{!agent.last_ip && !agent.local_ips && (
								<p className="text-sm text-slate-400 dark:text-slate-500">&mdash;</p>
							)}
						</div>
					</div>
					<div>
						<p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">Workspaces</p>
						<p className="text-sm font-medium text-slate-900 dark:text-white mt-1">{instances.length}</p>
					</div>
					<div>
						<p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">Connected Since</p>
						<p className="text-sm font-medium text-slate-900 dark:text-white mt-1">{formatDate(agent.created_at)}</p>
					</div>
				</div>
			</div>

			{/* Machine-level Terminal — shown for agent update/restart, install, stop-all */}
			{showLogs && agentId && terminalContext === 'machine' && (
				<div className="mb-6">
					<LogViewer
						agentId={agentId}
						commandId={activeCommandId}
						isActive={showLogs}
						mode={logMode}
						onClose={() => {
							setShowLogs(false);
							setActiveCommandId(null);
							setTerminalContext(null);
						}}
					/>
				</div>
			)}

			{/* Agent Logs Viewer — one-shot fetch of agent disk log */}
			{showAgentLogs && agentId && (
				<div className="mb-6">
					<AgentLogViewer
						title="Connector Logs"
						fetchUrl={`${API_URL}/api/v1/agents/${agentId}/agent-logs?lines=500`}
						onClose={() => setShowAgentLogs(false)}
					/>
				</div>
			)}

			{/* Bot Instances Section */}
			<div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl">
				<div className="p-6 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
					<div>
						<h3 className="text-lg font-semibold text-slate-900 dark:text-white">Workspaces</h3>
						<p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
							Manage bots and workspaces running on this device
						</p>
					</div>
					{agent.status === 'online' && (
						<button
							onClick={() => setShowAddInstanceModal(true)}
							className="px-4 py-2 text-sm bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-200 cursor-pointer flex items-center space-x-2"
						>
							<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
							</svg>
							<span>Add Workspace</span>
						</button>
					)}
				</div>

				{/* Bot Type Tabs — only shown when multiple types exist */}
				{botTypes.length > 1 && (
					<div className="border-b border-slate-100 dark:border-slate-800">
						<nav className="flex -mb-px px-6 overflow-x-auto">
							<button
								onClick={() => setActiveBotType('all')}
								className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors cursor-pointer mr-1 ${
									activeBotType === 'all'
										? 'border-primary-600 text-primary-600 dark:text-primary-400'
										: 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:border-slate-300'
								}`}
							>
								All ({instances.length})
							</button>
							{botTypes.map((type) => (
								<button
									key={type}
									onClick={() => setActiveBotType(type)}
									className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors cursor-pointer mr-1 ${
										activeBotType === type
											? 'border-primary-600 text-primary-600 dark:text-primary-400'
											: 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:border-slate-300'
									}`}
								>
									{getBotTypeLabel(type)} ({instancesByType[type].length})
								</button>
							))}
						</nav>
					</div>
				)}

				{instances.length === 0 ? (
					<div className="p-12 text-center">
						<div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-2xl mb-4">
							<svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
							</svg>
						</div>
						<h4 className="text-lg font-medium text-slate-900 dark:text-white mb-2">No workspaces found on this device</h4>
						<p className="text-slate-600 dark:text-slate-400">
							{agent.status === 'online'
								? 'Use the Install button above to set up OpenClaw on this device'
								: 'Connect the connector to manage workspaces'
							}
						</p>
					</div>
				) : (
					<div className="divide-y divide-slate-100 dark:divide-slate-800">
						{displayedInstances.map((instance) => (
							<div key={instance.instance_id}>
								{/* Instance Row */}
								<div className="p-6 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
									<div className="flex items-center justify-between">
										<div className="flex items-center space-x-4">
											<div className={`p-2 rounded-xl ${
												instance.agent_status === 'offline'
													? 'bg-slate-100 dark:bg-slate-800'
													: instance.running_status === 'running'
													? 'bg-green-100 dark:bg-green-900/30'
													: instance.running_status === 'error'
													? 'bg-red-100 dark:bg-red-900/30'
													: 'bg-slate-100 dark:bg-slate-800'
											}`}>
												{getBotTypeIcon(instance.bot_type, `w-6 h-6 ${
													instance.agent_status === 'offline'
														? 'text-slate-400'
														: instance.running_status === 'running'
														? 'text-green-600 dark:text-green-400'
														: instance.running_status === 'error'
														? 'text-red-600 dark:text-red-400'
														: 'text-slate-400'
												}`)}
											</div>
											<div>
												<div className="flex items-center space-x-3">
													{renamingInstanceId === instance.instance_id ? (
														<input
															type="text"
															value={renameValue}
															onChange={(e) => setRenameValue(e.target.value)}
															onBlur={() => {
																if (renameValue.trim() && renameValue.trim() !== (instance.instance_name || 'unnamed')) {
																	handleRenameInstance(instance.instance_id, renameValue.trim());
																} else {
																	setRenamingInstanceId(null);
																}
															}}
															onKeyDown={(e) => {
																if (e.key === 'Enter') {
																	if (renameValue.trim() && renameValue.trim() !== (instance.instance_name || 'unnamed')) {
																		handleRenameInstance(instance.instance_id, renameValue.trim());
																	} else {
																		setRenamingInstanceId(null);
																	}
																} else if (e.key === 'Escape') {
																	setRenamingInstanceId(null);
																}
															}}
															autoFocus
															className="px-2 py-1 text-sm font-medium border border-primary-500 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none w-48"
														/>
													) : (
														<p className="font-medium text-slate-900 dark:text-white">
															{instance.instance_name || 'unnamed'}
														</p>
													)}
													{renamingInstanceId !== instance.instance_id && (
														<button
															onClick={(e) => {
																e.stopPropagation();
																setRenamingInstanceId(instance.instance_id);
																setRenameValue(instance.instance_name || 'unnamed');
															}}
															className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors cursor-pointer"
															title="Rename workspace"
														>
															<svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
															</svg>
														</button>
													)}
													{getStatusBadge(instance.running_status, instance.agent_status)}
													{/* Bot type badge when viewing "All" tab */}
													{activeBotType === 'all' && botTypes.length > 1 && (
														<span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
															{getBotTypeLabel(instance.bot_type || 'openclaw')}
														</span>
													)}
												</div>
												<p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
													{getBotTypeLabel(instance.bot_type || 'openclaw')} v{instance.clawdbot_version}
												</p>
												<p className="text-sm text-slate-500 dark:text-slate-400 mt-1 font-mono">
													{instance.install_path}
												</p>
											</div>
										</div>

										{/* Action Buttons */}
										<div className="flex items-center space-x-2">
											<Link
												to={`/instances/${instance.instance_id}/config`}
												className="px-3 py-2 text-sm font-medium rounded-lg transition-colors flex items-center space-x-1 text-primary-700 dark:text-primary-300 bg-primary-100 dark:bg-primary-900/30 hover:bg-primary-200 dark:hover:bg-primary-900/50"
											>
												<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
												</svg>
												<span>Configure</span>
											</Link>

											{instance.running_status === 'running' ? (
												<>
													<button
														onClick={() => handleLifecycleAction(instance.instance_id, 'restart')}
														disabled={actionLoading === instance.instance_id || agent.status !== 'online'}
														className="px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-50 rounded-lg transition-colors cursor-pointer disabled:cursor-not-allowed flex items-center space-x-1.5"
													>
														{actionLoading === instance.instance_id ? (
															<>
																<div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-slate-600 dark:border-slate-300"></div>
																<span>Restarting...</span>
															</>
														) : (
															<span>Restart</span>
														)}
													</button>
													<button
														onClick={() => handleLifecycleAction(instance.instance_id, 'stop')}
														disabled={actionLoading === instance.instance_id || agent.status !== 'online'}
														className="px-3 py-2 text-sm font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 disabled:opacity-50 rounded-lg transition-colors cursor-pointer disabled:cursor-not-allowed flex items-center space-x-1.5"
													>
														{actionLoading === instance.instance_id ? (
															<>
																<div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-red-600 dark:border-red-300"></div>
																<span>Stopping...</span>
															</>
														) : (
															<span>Stop</span>
														)}
													</button>
												</>
											) : instance.running_status === 'error' ? (
												<button
													onClick={() => handleLifecycleAction(instance.instance_id, 'stop')}
													disabled={actionLoading === instance.instance_id || agent.status !== 'online'}
													className="px-3 py-2 text-sm font-medium text-white bg-red-600 dark:bg-red-700 hover:bg-red-700 dark:hover:bg-red-800 disabled:opacity-50 rounded-lg transition-colors cursor-pointer disabled:cursor-not-allowed flex items-center space-x-1.5"
												>
													{actionLoading === instance.instance_id ? (
														<>
															<div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>
															<span>Force Stopping...</span>
														</>
													) : (
														<span>Force Stop</span>
													)}
												</button>
											) : (
												<button
													onClick={() => handleLifecycleAction(instance.instance_id, 'start')}
													disabled={actionLoading === instance.instance_id || agent.status !== 'online'}
													className="px-3 py-2 text-sm font-medium text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-900/50 disabled:opacity-50 rounded-lg transition-colors cursor-pointer disabled:cursor-not-allowed flex items-center space-x-1.5"
												>
													{actionLoading === instance.instance_id ? (
														<>
															<div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-green-600 dark:border-green-300"></div>
															<span>Starting...</span>
														</>
													) : (
														<span>Start</span>
													)}
												</button>
											)}

											<button
												onClick={() => setInstanceLogsId(instanceLogsId === instance.instance_id ? null : instance.instance_id)}
												disabled={agent.status !== 'online'}
												className="px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-50 rounded-lg transition-colors cursor-pointer disabled:cursor-not-allowed flex items-center space-x-1.5"
												title="View workspace logs"
											>
												<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
												</svg>
												<span>Logs</span>
											</button>
											<button
												onClick={() => setShowUninstallConfirm(instance.instance_id)}
												disabled={actionLoading === instance.instance_id}
												className="px-3 py-2 text-sm font-medium text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/30 disabled:opacity-50 rounded-lg transition-colors cursor-pointer disabled:cursor-not-allowed"
												title="Remove this workspace"
											>
												<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
												</svg>
											</button>
										</div>
									</div>

									{/* Security Risk Indicators */}
									{instanceRisks[instance.instance_id] && (
										<div className="flex items-center flex-wrap gap-2 mt-3">
											<SecurityBadge
												risk={instanceRisks[instance.instance_id].threatRisk}
												label={instanceRisks[instance.instance_id].threatDetectionEnabled ? 'Threat Protection' : 'No Threat Protection'}
												icon="shield"
											/>
											<SecurityBadge
												risk={instanceRisks[instance.instance_id].pamRisk}
												label={instanceRisks[instance.instance_id].pamEnabled ? 'Access Control Active' : 'No Access Control'}
												icon="lock"
											/>
											{instanceRisks[instance.instance_id].managedChannels.length > 0 && (
												<SecurityBadge
													risk="good"
													label={`${instanceRisks[instance.instance_id].managedChannels.length} managed channel${instanceRisks[instance.instance_id].managedChannels.length > 1 ? 's' : ''}`}
													icon="shield"
												/>
											)}
											{instanceRisks[instance.instance_id].directChannels.length > 0 && (
												<SecurityBadge
													risk="critical"
													label={`${instanceRisks[instance.instance_id].directChannels.length} direct channel${instanceRisks[instance.instance_id].directChannels.length > 1 ? 's' : ''}`}
													icon="warning"
												/>
											)}
											{instanceRisks[instance.instance_id].assessedAt != null && (
												<span className="text-xs text-slate-500 dark:text-slate-400">
													Scanned {(() => {
														const mins = Math.round((Date.now() / 1000 - instanceRisks[instance.instance_id].assessedAt!) / 60);
														if (mins < 1) return 'just now';
														if (mins < 60) return `${mins} min ago`;
														return `${Math.round(mins / 60)} hr ago`;
													})()}
												</span>
											)}
										</div>
									)}

									{/* Team Channel Key Assignments */}
								{(() => {
									const instanceKeys = channelKeyAssignments.filter(
										(a) => a.instance_id === instance.instance_id
									);
									return instanceKeys.length > 0 ? (
										<div className="flex items-center flex-wrap gap-2 mt-3">
											<span className="text-xs text-slate-500 dark:text-slate-400 font-medium">Team Keys:</span>
											{instanceKeys.map((a) => (
												<span
													key={`${a.channel_type}-${a.key_id}`}
													className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300"
												>
													<svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
													</svg>
													{a.channel_type}: {a.label}
												</span>
											))}
											<Link
												to="/settings"
												className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
											>
												Manage
											</Link>
										</div>
									) : (
										<div className="flex items-start gap-2 mt-3 p-2.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
											<svg className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
											</svg>
											<div className="text-xs text-amber-800 dark:text-amber-300">
												<span className="font-semibold">No team channel key assigned</span> — this workspace won't receive proxied channel messages. <Link to="/settings" className="text-amber-700 dark:text-amber-200 underline hover:no-underline font-medium">Assign keys in Settings</Link>
											</div>
										</div>
									);
								})()}

								{/* Instance Details */}
									<div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
										<div>
											<p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">Workspace ID</p>
											<p className="text-slate-700 dark:text-slate-300 font-mono text-xs mt-1">{instance.instance_id.slice(0, 8)}...</p>
										</div>
										<div>
											<p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">Version</p>
											<p className="text-slate-700 dark:text-slate-300 mt-1">{instance.clawdbot_version}</p>
										</div>
										<div>
											<p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">Gateway Port</p>
											<p className="text-slate-700 dark:text-slate-300 mt-1">{instance.gateway_port || 18789}</p>
										</div>
										<div>
											<p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">Discovered</p>
											<p className="text-slate-700 dark:text-slate-300 mt-1">{formatDate(instance.discovered_at || instance.created_at)}</p>
										</div>
										<div>
											<p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">Status</p>
											<p className="text-slate-700 dark:text-slate-300 mt-1 capitalize">
												{instance.agent_status === 'offline' ? 'unavailable' : instance.running_status}
											</p>
										</div>
									</div>

									{/* Instance-level Terminal — shown for start/stop/restart of this instance */}
									{showLogs && agentId && terminalContext === instance.instance_id && (
										<div className="mt-4">
											<LogViewer
												agentId={agentId}
												commandId={activeCommandId}
												isActive={showLogs}
												mode={logMode}
												onClose={() => {
													setShowLogs(false);
													setActiveCommandId(null);
													setTerminalContext(null);
												}}
											/>
										</div>
									)}

									{/* Instance Logs Viewer — one-shot fetch of gateway log */}
									{instanceLogsId === instance.instance_id && (
										<div className="mt-4">
											<AgentLogViewer
												title="Workspace Logs"
												fetchUrl={`${API_URL}/api/v1/instances/${instance.instance_id}/instance-logs?lines=500`}
												onClose={() => setInstanceLogsId(null)}
											/>
										</div>
									)}
								</div>

							</div>
						))}
					</div>
				)}

			</div>

			{/* Instance-level terminal rendered inline — see instance cards above */}

			{/* Uninstall Instance Confirmation Modal */}
			{showUninstallConfirm && (
				<div className="fixed inset-0 z-50 flex items-center justify-center">
					<div
						className="absolute inset-0 bg-black/50 backdrop-blur-sm"
						onClick={() => setShowUninstallConfirm(null)}
					/>
					<div className="relative bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
						<div className="flex items-center space-x-4 mb-4">
							<div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-xl">
								<svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
								</svg>
							</div>
							<div>
								<h3 className="text-lg font-semibold text-slate-900 dark:text-white">Remove Workspace</h3>
								<p className="text-sm text-slate-500 dark:text-slate-400">This will stop and remove the workspace</p>
							</div>
						</div>
						<p className="text-slate-600 dark:text-slate-400 mb-6">
							This will <strong className="text-slate-900 dark:text-white">stop the running gateway</strong> and remove the workspace record, including its policies and credentials. The installed files on the device will remain.
						</p>
						<div className="flex items-center space-x-3">
							<button
								onClick={() => setShowUninstallConfirm(null)}
								className="flex-1 px-4 py-2 text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-xl font-medium transition-colors cursor-pointer"
							>
								Cancel
							</button>
							<button
								onClick={() => handleUninstallInstance(showUninstallConfirm)}
								disabled={actionLoading === showUninstallConfirm}
								className="flex-1 px-4 py-2 text-white bg-red-600 hover:bg-red-700 disabled:bg-red-400 disabled:cursor-not-allowed rounded-xl font-medium transition-colors cursor-pointer"
							>
								{actionLoading === showUninstallConfirm ? 'Removing...' : 'Yes, Remove Workspace'}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Clean Install Confirmation Modal */}
			{showCleanConfirm && (
				<div className="fixed inset-0 z-50 flex items-center justify-center">
					<div
						className="absolute inset-0 bg-black/50 backdrop-blur-sm"
						onClick={() => setShowCleanConfirm(false)}
					/>
					<div className="relative bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
						<div className="flex items-center space-x-4 mb-4">
							<div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-xl">
								<svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
								</svg>
							</div>
							<div>
								<h3 className="text-lg font-semibold text-slate-900 dark:text-white">Clean Install OpenClaw</h3>
								<p className="text-sm text-slate-500 dark:text-slate-400">This will wipe all existing OpenClaw configuration</p>
							</div>
						</div>
						<p className="text-slate-600 dark:text-slate-400 mb-6">
							This will <strong className="text-slate-900 dark:text-white">remove all OpenClaw configuration and data</strong>, including gateway tokens, channel settings, and credentials. A fresh configuration will be generated.
						</p>
						<div className="flex items-center space-x-3">
							<button
								onClick={() => setShowCleanConfirm(false)}
								className="flex-1 px-4 py-2 text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-xl font-medium transition-colors cursor-pointer"
							>
								Cancel
							</button>
							<button
								onClick={() => handleInstall('clean')}
								disabled={installing}
								className="flex-1 px-4 py-2 text-white bg-red-600 hover:bg-red-700 disabled:bg-red-400 disabled:cursor-not-allowed rounded-xl font-medium transition-colors cursor-pointer"
							>
								{installing ? 'Installing...' : 'Yes, Clean Install OpenClaw'}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Delete Confirmation Modal */}
			{showDeleteModal && (
				<div className="fixed inset-0 z-50 flex items-center justify-center">
					<div
						className="absolute inset-0 bg-black/50 backdrop-blur-sm"
						onClick={() => !deleting && setShowDeleteModal(false)}
					/>
					<div className="relative bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
						<div className="flex items-center space-x-4 mb-4">
							<div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-xl">
								<svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
								</svg>
							</div>
							<div>
								<h3 className="text-lg font-semibold text-slate-900 dark:text-white">Remove Device</h3>
								<p className="text-sm text-slate-500 dark:text-slate-400">This action cannot be undone</p>
							</div>
						</div>

						<div className="mb-6">
							<p className="text-slate-600 dark:text-slate-400 mb-4">
								This will permanently remove <strong className="text-slate-900 dark:text-white">{agent.hostname}</strong> from Clawdgate, including:
							</p>
							<ul className="text-sm text-slate-600 dark:text-slate-400 space-y-2 ml-4">
								<li className="flex items-center space-x-2">
									<span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
									<span>All workspace records ({instances.length} workspace{instances.length !== 1 ? 's' : ''})</span>
								</li>
								<li className="flex items-center space-x-2">
									<span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
									<span>Command history and queue</span>
								</li>
								<li className="flex items-center space-x-2">
									<span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
									<span>Connector registration</span>
								</li>
							</ul>
						</div>

						<div className="mb-6">
							<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
								Type <strong>{agent.hostname}</strong> to confirm:
							</label>
							<input
								type="text"
								value={deleteConfirmText}
								onChange={(e) => setDeleteConfirmText(e.target.value)}
								placeholder={agent.hostname}
								className="w-full px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
								disabled={deleting}
							/>
						</div>

						<div className="flex items-center space-x-3">
							<button
								onClick={() => {
									setShowDeleteModal(false);
									setDeleteConfirmText('');
								}}
								disabled={deleting}
								className="flex-1 px-4 py-2 text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-50 rounded-xl font-medium transition-colors cursor-pointer disabled:cursor-not-allowed"
							>
								Cancel
							</button>
							<button
								onClick={handleDeleteMachine}
								disabled={deleting || deleteConfirmText !== agent.hostname}
								className="flex-1 px-4 py-2 text-white bg-red-600 hover:bg-red-700 disabled:bg-red-400 disabled:cursor-not-allowed rounded-xl font-medium transition-colors cursor-pointer flex items-center justify-center space-x-2"
							>
								{deleting ? (
									<>
										<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
										<span>Removing...</span>
									</>
								) : (
									<span>Remove Device</span>
								)}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Add Instance Modal */}
			{showAddInstanceModal && (
				<div className="fixed inset-0 z-50 flex items-center justify-center">
					<div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => !addingInstance && setShowAddInstanceModal(false)} />
					<div className="relative bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
						<h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Add Workspace</h3>
						<div className="space-y-4">
							<div>
								<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Workspace Name</label>
								<input
									type="text"
									value={newInstanceName}
									onChange={(e) => setNewInstanceName(e.target.value)}
									placeholder="my-workspace"
									pattern="[a-zA-Z0-9_-]{1,64}"
									className="w-full px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
									disabled={addingInstance}
								/>
								<p className="text-xs text-slate-400 dark:text-slate-500 mt-1">1-64 characters, letters, numbers, hyphens, underscores</p>
							</div>
							<div>
								<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Type</label>
								<div className="space-y-1.5">
									{BOT_TYPES.map((opt) => (
										<button
											key={opt.value}
											type="button"
											onClick={() => opt.available && !addingInstance && setNewInstanceType(opt.value)}
											disabled={!opt.available || addingInstance}
											className={`w-full text-left px-4 py-2.5 rounded-xl border text-sm font-medium transition-all ${
												newInstanceType === opt.value && opt.available
													? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 ring-1 ring-primary-500'
													: opt.available
													? 'border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-primary-300 dark:hover:border-primary-700 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer'
													: 'border-slate-100 dark:border-slate-800 text-slate-400 dark:text-slate-600 cursor-not-allowed bg-slate-50 dark:bg-slate-800/50'
											}`}
										>
											{opt.label}
											{!opt.available && (
												<span className="ml-2 text-xs text-slate-400 dark:text-slate-600">(coming soon)</span>
											)}
										</button>
									))}
								</div>
								{newInstanceType === 'openclaw' && agent && agent.openclaw_installed !== 1 && (
									<p className="text-xs text-amber-600 dark:text-amber-400 mt-2 flex items-center space-x-1">
										<svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
											<path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
										</svg>
										<span>OpenClaw is not installed on this device. Use the Install action first.</span>
									</p>
								)}
							</div>

							{/* Workspace step — claude_code / codex only */}
							{botTypeRequiresWorkspace(newInstanceType) && (
								<div>
									<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Workspace</label>
									<div className="inline-flex rounded-xl bg-slate-100 dark:bg-slate-800 p-1 mb-3">
										<button
											type="button"
											onClick={() => setNewWorkspaceMode('local')}
											disabled={addingInstance}
											className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all cursor-pointer ${
												newWorkspaceMode === 'local'
													? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-sm'
													: 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
											}`}
										>
											Local directory
										</button>
										<button
											type="button"
											onClick={() => setNewWorkspaceMode('remote')}
											disabled={addingInstance}
											className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all cursor-pointer ${
												newWorkspaceMode === 'remote'
													? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-sm'
													: 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
											}`}
										>
											Remote share
										</button>
									</div>
									{newWorkspaceMode === 'local' ? (
										<WorkspacePicker agentId={agentId ?? ''} value={newWorkspaceDir} onChange={setNewWorkspaceDir} disabled={addingInstance} />
									) : (
										<ShareUrlInput value={newShareUrl} onChange={setNewShareUrl} disabled={addingInstance} />
									)}
								</div>
							)}
						</div>
						<div className="flex items-center space-x-3 mt-6">
							<button
								onClick={() => {
									setShowAddInstanceModal(false);
									setNewInstanceName('');
									setNewInstanceType('openclaw');
									setNewWorkspaceMode('local');
									setNewWorkspaceDir('');
									setNewShareUrl('');
								}}
								disabled={addingInstance}
								className="flex-1 px-4 py-2 text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-50 rounded-xl font-medium transition-colors cursor-pointer disabled:cursor-not-allowed"
							>
								Cancel
							</button>
							<button
								onClick={handleAddInstance}
								disabled={
									addingInstance ||
									!newInstanceName.trim() ||
									!/^[a-zA-Z0-9_-]{1,64}$/.test(newInstanceName) ||
									(newInstanceType === 'openclaw' && agent?.openclaw_installed !== 1) ||
									(botTypeRequiresWorkspace(newInstanceType) && newWorkspaceMode === 'local' && !newWorkspaceDir.trim()) ||
									(botTypeRequiresWorkspace(newInstanceType) && newWorkspaceMode === 'remote' && !newShareUrl.trim())
								}
								className="flex-1 px-4 py-2 text-white bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 disabled:from-slate-400 disabled:to-slate-500 rounded-xl font-medium transition-all cursor-pointer disabled:cursor-not-allowed flex items-center justify-center space-x-2"
							>
								{addingInstance ? (
									<>
										<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
										<span>Creating...</span>
									</>
								) : (
									<span>Create Workspace</span>
								)}
							</button>
						</div>
					</div>
				</div>
			)}
		</Layout>
	);
}
